import {
  clamp,
  createLoop,
  drawBird,
  fitCanvas,
  lerp,
  mulberry32,
  pointerTracker,
} from "./shared.js";

function heartPath(ctx, x, y, size, pulse = 0) {
  const s = size * (1 + pulse * 0.05);
  ctx.beginPath();
  ctx.moveTo(x, y + s * 0.42);
  ctx.bezierCurveTo(x - s * 0.72, y, x - s * 0.5, y - s * 0.45, x, y - s * 0.12);
  ctx.bezierCurveTo(x + s * 0.5, y - s * 0.45, x + s * 0.72, y, x, y + s * 0.42);
  ctx.closePath();
}

export function mountHeart(root, settings) {
  const canvas = document.createElement("canvas");
  canvas.className = "scene-canvas";
  root.append(canvas);
  const pointer = pointerTracker(canvas);
  const random = mulberry32(settings.seed);
  const butterflies = Array.from({ length: Math.round(56 * settings.density) }, (_, index) => ({
    angle: random() * Math.PI * 2,
    radius: 45 + random() * 190,
    speed: 0.25 + random() * 0.6,
    offset: random() * 20,
    size: 0.55 + random() * 0.7,
    drift: (random() - 0.5) * 0.7,
    index,
  }));
  const cracks = Array.from({ length: 28 }, (_, index) => ({
    angle: (index / 28) * Math.PI * 2 + (random() - 0.5) * 0.16,
    length: 60 + random() * 230,
    bend: (random() - 0.5) * 35,
  }));

  return createLoop((dt, time) => {
    const { ctx, width, height } = fitCanvas(canvas, settings.quality);
    const cx = width * 0.5;
    const cy = height * 0.48;
    ctx.fillStyle = "#eef1ed";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "rgba(26,30,28,.13)";
    ctx.lineWidth = 0.7;
    cracks.forEach((crack) => {
      const x1 = cx + Math.cos(crack.angle) * 45;
      const y1 = cy + Math.sin(crack.angle) * 36;
      const x2 = cx + Math.cos(crack.angle) * crack.length;
      const y2 = cy + Math.sin(crack.angle) * crack.length;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.quadraticCurveTo((x1 + x2) / 2 + crack.bend, (y1 + y2) / 2 - crack.bend, x2, y2);
      ctx.stroke();
    });

    const pulse = Math.max(0, Math.sin(time * 2.4));
    ctx.fillStyle = "#151918";
    heartPath(ctx, cx, cy, Math.min(width, height) * 0.28, pulse);
    ctx.fill();

    const targetX = pointer.active ? pointer.x * width : cx + Math.cos(time * 0.65) * width * 0.2;
    const targetY = pointer.active ? pointer.y * height : cy + Math.sin(time * 0.8) * height * 0.2;
    butterflies.forEach((butterfly) => {
      butterfly.angle += dt * butterfly.speed;
      const orbitX = Math.cos(butterfly.angle + butterfly.offset) * butterfly.radius;
      const orbitY = Math.sin(butterfly.angle * 1.17 + butterfly.offset) * butterfly.radius * 0.65;
      const pull = 0.22 + 0.18 * Math.sin(time + butterfly.offset);
      const x = lerp(cx + orbitX, targetX, pull);
      const y = lerp(cy + orbitY, targetY, pull);
      const angle = Math.atan2(targetY - y, targetX - x);
      drawBird(ctx, x, y, butterfly.size, angle, butterfly.index % 5 === 0 ? "#b3372b" : "#d3a91e", 0.5 + Math.sin(time * 9 + butterfly.offset) * 0.28);
    });

    ctx.strokeStyle = "rgba(179,55,43,.68)";
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    ctx.moveTo(cx, cy + Math.min(width, height) * 0.11);
    ctx.quadraticCurveTo(width * 0.58, height * 0.78, targetX, targetY);
    ctx.stroke();
  }, settings);
}

function point(x, y, locked = false) {
  return { x, y, oldX: x, oldY: y, locked };
}

export function mountPuppet(root, settings) {
  const canvas = document.createElement("canvas");
  canvas.className = "scene-canvas";
  root.append(canvas);
  const pointer = pointerTracker(canvas);
  let initialized = false;
  let joints = [];
  let sticks = [];
  const make = (width, height) => {
    const cx = width / 2;
    const top = height * 0.15;
    joints = [
      point(cx, top, true),
      point(cx, top + 125),
      point(cx, top + 210),
      point(cx - 70, top + 170),
      point(cx + 70, top + 170),
      point(cx - 48, top + 300),
      point(cx + 48, top + 300),
      point(cx - 58, top + 420),
      point(cx + 58, top + 420),
    ];
    sticks = [
      [0, 1, 125],
      [1, 2, 85],
      [1, 3, 86],
      [1, 4, 86],
      [2, 5, 96],
      [2, 6, 96],
      [5, 7, 120],
      [6, 8, 120],
    ];
    initialized = true;
  };

  return createLoop((dt, time) => {
    const { ctx, width, height } = fitCanvas(canvas, settings.quality);
    if (!initialized) make(width, height);
    ctx.fillStyle = "#e9e1ce";
    ctx.fillRect(0, 0, width, height);
    const controlX = pointer.active ? pointer.x * width : width / 2 + Math.sin(time * 0.7) * width * 0.2;
    const controlY = pointer.active ? pointer.y * height * 0.65 : height * 0.12;
    joints[0].x = controlX;
    joints[0].y = controlY;

    for (const joint of joints) {
      if (joint.locked) continue;
      const vx = (joint.x - joint.oldX) * 0.992;
      const vy = (joint.y - joint.oldY) * 0.992;
      joint.oldX = joint.x;
      joint.oldY = joint.y;
      joint.x += vx;
      joint.y += vy + 760 * dt * dt;
    }
    for (let pass = 0; pass < 8; pass += 1) {
      sticks.forEach(([a, b, length]) => {
        const p1 = joints[a];
        const p2 = joints[b];
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const dist = Math.max(0.001, Math.hypot(dx, dy));
        const correction = (dist - length) / dist;
        if (!p1.locked) {
          p1.x += dx * correction * 0.5;
          p1.y += dy * correction * 0.5;
        }
        if (!p2.locked) {
          p2.x -= dx * correction * 0.5;
          p2.y -= dy * correction * 0.5;
        }
      });
    }

    ctx.strokeStyle = "rgba(23,20,15,.25)";
    ctx.lineWidth = 0.8;
    [3, 4, 7, 8].forEach((index) => {
      ctx.beginPath();
      ctx.moveTo(controlX + (index % 2 ? -36 : 36), controlY);
      ctx.lineTo(joints[index].x, joints[index].y);
      ctx.stroke();
    });
    ctx.strokeStyle = "#17140f";
    ctx.lineWidth = 7;
    ctx.lineCap = "round";
    sticks.slice(1).forEach(([a, b]) => {
      ctx.beginPath();
      ctx.moveTo(joints[a].x, joints[a].y);
      ctx.lineTo(joints[b].x, joints[b].y);
      ctx.stroke();
    });
    ctx.fillStyle = "#b3372b";
    ctx.beginPath();
    ctx.arc(joints[1].x, joints[1].y - 26, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#17140f";
    ctx.fillRect(controlX - 78, controlY - 4, 156, 8);
  }, settings);
}

export function mountControl(root, settings) {
  const canvas = document.createElement("canvas");
  canvas.className = "scene-canvas";
  root.append(canvas);
  const pointer = pointerTracker(canvas);
  const random = mulberry32(settings.seed + 44);
  const notes = Array.from({ length: Math.round(42 * settings.density) }, (_, index) => ({
    x: (index + 1) / 44,
    staff: index % 5,
    phase: random() * Math.PI * 2,
    vx: 0,
    vy: 0,
    activity: 0,
  }));

  return createLoop((dt, time) => {
    const { ctx, width, height } = fitCanvas(canvas, settings.quality);
    ctx.fillStyle = "#eee8d8";
    ctx.fillRect(0, 0, width, height);
    const top = height * 0.28;
    const gap = Math.min(34, height * 0.045);
    ctx.strokeStyle = "rgba(23,20,15,.46)";
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i += 1) {
      ctx.beginPath();
      ctx.moveTo(width * 0.08, top + i * gap);
      ctx.lineTo(width * 0.92, top + i * gap);
      ctx.stroke();
    }
    const px = pointer.x * width;
    const py = pointer.y * height;
    notes.forEach((note, index) => {
      const homeX = note.x * width;
      const homeY = top + note.staff * gap;
      const dist = Math.hypot(homeX - px, homeY - py);
      if (pointer.active && dist < 170) note.activity = clamp(note.activity + dt * 5, 0, 1);
      else note.activity = clamp(note.activity - dt * 0.65, 0, 1);
      const wingX = Math.sin(time * 1.7 + note.phase) * (24 + note.activity * 90);
      const wingY = Math.cos(time * 2.1 + note.phase) * (12 + note.activity * 70);
      const x = homeX + wingX * note.activity;
      const y = homeY + wingY * note.activity;
      if (note.activity > 0.18) {
        drawBird(ctx, x, y, 0.7 + note.activity * 0.5, Math.atan2(wingY, wingX), "#17140f", 0.5 + Math.sin(time * 8 + index) * 0.3);
        ctx.strokeStyle = `rgba(179,55,43,${note.activity * 0.44})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(homeX, homeY);
        ctx.quadraticCurveTo((homeX + x) / 2, y - 30, x, y);
        ctx.stroke();
      } else {
        ctx.fillStyle = "#17140f";
        ctx.beginPath();
        ctx.ellipse(x, y, 5, 4, -0.35, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#17140f";
        ctx.beginPath();
        ctx.moveTo(x + 4, y);
        ctx.lineTo(x + 4, y - 22);
        ctx.stroke();
      }
    });
    ctx.fillStyle = "#b3372b";
    ctx.font = "700 13px 'Courier New', monospace";
    ctx.fillText("CONTROL / RELEASE", width * 0.08, top - 48);
  }, settings);
}

export function mountCurtain(root, settings) {
  const canvas = document.createElement("canvas");
  canvas.className = "scene-canvas";
  root.append(canvas);
  const pointer = pointerTracker(canvas);
  let strands = [];
  let previousSize = "";

  return createLoop((dt, time) => {
    const { ctx, width, height } = fitCanvas(canvas, settings.quality);
    const sizeKey = `${Math.round(width)}x${Math.round(height)}:${settings.density}`;
    if (sizeKey !== previousSize) {
      const cols = Math.max(16, Math.round(30 * settings.density));
      const rows = Math.max(22, Math.round(42 * settings.density));
      strands = Array.from({ length: cols }, (_, col) =>
        Array.from({ length: rows }, (_, row) => ({
          homeX: (col / (cols - 1)) * width,
          homeY: (row / (rows - 1)) * height,
          x: (col / (cols - 1)) * width,
          y: (row / (rows - 1)) * height,
          vx: 0,
          vy: 0,
        })),
      );
      previousSize = sizeKey;
    }
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#f3ead5");
    gradient.addColorStop(0.62, "#d9b98a");
    gradient.addColorStop(1, "#9c633f");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    const px = pointer.x * width;
    const py = pointer.y * height;
    strands.forEach((strand, col) => {
      strand.forEach((bead, row) => {
        const wind = Math.sin(time * 1.1 + row * 0.18 + col * 0.13) * (5 + row * 0.22);
        const dx = bead.x - px;
        const dy = bead.y - py;
        const dist = Math.max(18, Math.hypot(dx, dy));
        if (pointer.active && dist < 95) bead.vx += (dx / dist) * (95 - dist) * dt * 8;
        bead.vx += (bead.homeX + wind - bead.x) * dt * 10;
        bead.vy += (bead.homeY - bead.y) * dt * 12;
        bead.vx *= 0.91;
        bead.vy *= 0.91;
        bead.x += bead.vx;
        bead.y += bead.vy;
      });
    });
    ctx.globalCompositeOperation = "multiply";
    strands.forEach((strand) => {
      ctx.strokeStyle = "rgba(85,48,28,.21)";
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      strand.forEach((bead, row) => {
        if (row === 0) ctx.moveTo(bead.x, bead.y);
        else ctx.lineTo(bead.x, bead.y);
      });
      ctx.stroke();
      strand.forEach((bead, row) => {
        const radius = 2.2 + Math.sin(row * 0.8) * 0.6;
        ctx.fillStyle = row % 6 === 0 ? "rgba(176,52,39,.56)" : "rgba(83,50,30,.34)";
        ctx.beginPath();
        ctx.arc(bead.x, bead.y, radius, 0, Math.PI * 2);
        ctx.fill();
      });
    });
    ctx.globalCompositeOperation = "source-over";
  }, settings);
}
